import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PostList from 'components/PostList';
import { getLayout } from 'components/Layouts/MainLayout';
import SpaceEmptyState from 'components/SpaceEmptyState/SpaceEmptyState';
import CommunityHeader from 'components/CommunityHeader/CommunityHeader';
import SpaceInfo from 'components/SpaceInfo/SpaceInfo';
import WelcomeToSpace from 'components/WelcomeToSpace';
import { useQuery } from '@apollo/client';
import { goToAllSpaces } from 'utils/routerHelpers';
import { useRouter } from 'next/router';
import { useCommunityContext } from 'components/CommunityContext';
import { Space } from 'interfaces/space';
import { SPACE_INFO_QUERY } from 'schema/Space/queries';
import CreateNewPost from 'components/CreateNewPost';
import Layout from 'components/new/layout';

interface SpacePageProps {
  postListState: {
    useCacheFirst: boolean;
  };
}

const SpacePostsPage = ({ postListState }: SpacePageProps) => {
  const { t } = useTranslation();
  const [space, setSpace] = useState<Space | null>(null);
  const { community } = useCommunityContext();
  const router = useRouter();
  const { spaceId } = router.query;

  const { data } = useQuery(SPACE_INFO_QUERY, {
    variables: {
      communityId: community.id,
      spaceId,
    },
    notifyOnNetworkStatusChange: true,
    skip: !spaceId,
  });

  useEffect(() => {
    if (data?.community) {
      setSpace(data.community?.communitySpace as Space);
    }
  }, [data]);

  if (!space) {
    return <></>;
  }

  const pageTitle = t('pages-space_title', 'Space', { name: '' });

  return (
    <>
      <CommunityHeader pageTitle={pageTitle} />
      <Layout.ContextHeader
        breadcrumb={{
          url: `/communities/${community.id}/spaces`,
          onClick: (e) => {
            e.preventDefault();
            goToAllSpaces(router, community.id);
          },
        }}
        pageTitle={pageTitle}
      />
      <Layout.PageContent>
        <SpaceInfo space={space} />
        {spaceId && <WelcomeToSpace />}

        <CreateNewPost selectedSpace={space} />
        <PostList
          customEmptyState={<SpaceEmptyState />}
          emptyMessage={t(
            'pages-spaces-post_list_empty_message',
            'Start a discussion by creating a new post from above.'
          )}
          emptyTitle={t("Looks like you'll be the first!")}
          sharedState={postListState}
          t={t}
        />
      </Layout.PageContent>
    </>
  );
};

SpacePostsPage.getLayout = getLayout;

export default SpacePostsPage;
